import {Timestamp} from 'firebase/firestore';
import {
    NahausReminder,
    ReminderContextTypeOptions,
    ReminderStatusOptions,
    reminderTypes,
    ReminderTriggerModeOptions
} from 'app/modules/admin/identity/models/nahausReminder.interface';
import {NahausTask, SharingOptions} from 'app/modules/admin/tasks/models/tasks.types';
import {VAT_DEADLINE_COPY} from '@vat/vat-copy';
import {formatDateDE, periodLabel} from '../export/vat-format';

/**
 * Reine Helfer der Fristen-Aufgaben (Spec 9): Titel, Vorlauf, Aufgaben- und Erinnerungs-Payload.
 * Kein Angular, kein Firestore-Zugriff – Datum und Nutzer kommen als Parameter.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const VAT_DEADLINE_LEAD_DAYS = [7, 14] as const;
export type VatDeadlineLeadDays = typeof VAT_DEADLINE_LEAD_DAYS[number];

export interface VatDeadlineRecipient {
    id: string;
    displayName: string;
    email: string | null;
}

export const deadlinePeriodKey = (year: number, periodKey: string): string => `${year}_${periodKey}`;

export const buildDeadlineTaskTitle = (periodLabelText: string, landlordName: string): string =>
    VAT_DEADLINE_COPY.taskTitle(periodLabelText, landlordName);

/** Kalendertag der wirksamen Frist minus Vorlauf, 09:00 lokal (Date rollt Monatsgrenzen selbst). */
export const computeTaskDueDate = (dueDateEffective: Date, leadDays: number): Date =>
    new Date(dueDateEffective.getFullYear(), dueDateEffective.getMonth(), dueDateEffective.getDate() - leadDays, 9, 0, 0, 0);

export const buildReturnLink = (landlordID: string, returnID: string): string => `/umsatzsteuer/${landlordID}/voranmeldung/${returnID}`;

export interface DeadlineTaskParams {
    taskID: string;
    uid: string;
    customerID: string | null;
    landlordID: string;
    landlordName: string;
    returnID: string;
    year: number;
    periodKey: string;
    dueDateEffective: Date;
    leadDays: VatDeadlineLeadDays;
    regionHint: string | null;
    now: Date;
}

export const buildDeadlineTaskPayload = (p: DeadlineTaskParams): NahausTask => {
    const label = periodLabel(p.year, p.periodKey);
    return {
        id: p.taskID,
        uid: p.uid,
        createdBy: p.uid,
        customerID: p.customerID ?? null,
        createdAt: p.now,
        updatedAt: p.now,
        title: buildDeadlineTaskTitle(label, p.landlordName),
        notes: VAT_DEADLINE_COPY.taskNotes(formatDateDE(p.dueDateEffective), buildReturnLink(p.landlordID, p.returnID), p.regionHint),
        completed: false,
        hasReminder: true,
        dueDate: computeTaskDueDate(p.dueDateEffective, p.leadDays),
        priority: 1,
        sharingType: SharingOptions.PRIVATE,
    };
};

export type DeadlineReminderParams = DeadlineTaskParams & { reminderID: string; recipients: VatDeadlineRecipient[] };

/** NahausReminder kennt kein sourceFeature – Spec 9 verlangt es → zusätzliche Top-Level-Felder (A8). */
export type VatDeadlineReminder = NahausReminder & { sourceFeature: 'VAT'; vatReturnID: string };

export const buildDeadlineReminderPayload = (p: DeadlineReminderParams): VatDeadlineReminder => {
    const label = periodLabel(p.year, p.periodKey);
    const due = Timestamp.fromDate(computeTaskDueDate(p.dueDateEffective, p.leadDays));
    return {
        id: p.reminderID,
        title: buildDeadlineTaskTitle(label, p.landlordName),
        description: VAT_DEADLINE_COPY.reminderDescription(label, p.landlordName, formatDateDE(p.dueDateEffective)),
        recipientsIds: p.recipients.map(r => r.id),
        recipientsUserNames: p.recipients.map(r => r.displayName),
        recipientsEmails: p.recipients.map(r => r.email).filter((email): email is string => !!email),
        status: ReminderStatusOptions.ACTIVE,
        type: reminderTypes.BOTH,
        triggerMode: ReminderTriggerModeOptions.ONE_TIME,
        dueDate: due,
        nextDueDate: due,
        context: {type: ReminderContextTypeOptions.DEFAULT},
        createdBy: p.uid,
        sourceFeature: 'VAT',
        vatReturnID: p.returnID,
    };
};
